// libraries
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Form, FormInstance, Input, notification, Select } from 'antd';
import styled from 'styled-components';
import { CheckOutlined, DeleteFilled, EyeTwoTone, EyeInvisibleOutlined, SyncOutlined } from '@ant-design/icons';

// services
import * as GroupService from '../../../../../services/bb-api/group';
import * as MunicipalityService from '../../../../../services/bb-api/municipality';
import * as StatusService from '../../../../../services/bb-api/status';
import * as SponsorService from '../../../../../services/bb-api/sponsor';

// components
import { ImageTool } from '../../../../shared/image-tool';
import { DeleteModal } from '../../../../shared/delete-modal';

// utils
import { txt } from '../../../../../locales/i18n';
import { useDeviceSize } from '../../../../../hooks/device-size';

function handleSearch(target: string, setState: React.Dispatch<React.SetStateAction<Record<string, any>[]>>) {
	setState((previousValues: Record<string, any>[]) => {
		const filteredValues: Record<string, any>[] = previousValues.filter((value: Record<string, any>) => {
			return value.name.toLowerCase().includes(target.toLowerCase());
		});
		return filteredValues;
	});
}

const types: Record<string, any>[] = [
	{ id: 1, name: txt('group.type hidden') },
	{ id: 2, name: txt('group.type password') },
	{ id: 3, name: txt('group.type request') },
	{ id: 4, name: txt('group.type open') },
];

let municipalities: Record<string, any>[] = [];
let sponsors: Record<string, any>[] = [];

export function GroupPageForm(props: any): JSX.Element {
	const [group, setGroup] = props.useGroup;
	const [image, setImage] = useState<string>(group.image);
	const [initialImageSource, setInitialImageSource] = useState<string>();
	const [loading, setLoading] = useState<boolean>(true);
	const isMobileDevice: boolean = useDeviceSize();

	const [municipalitiesFiltered, setMunicipalities] = useState<Record<string, any>[]>([]);
	const [sponsorsFiltered, setSponsors] = useState<Record<string, any>[]>([]);
	const [statuses, setStatuses] = useState<Record<string, any>[]>([]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const { id }: Record<string, any> = useParams();
	const history = useHistory();

	const formRef = useRef<FormInstance>(null);
	const imageToolRef = useRef<any>(null);

	useEffect(() => {
		async function getResources(): Promise<void> {
			setLoading(true);

			try {
				municipalities = await MunicipalityService.query();
				sponsors = await SponsorService.query();
				const statuses: Record<string, any>[] = await StatusService.query();

				setStatuses(statuses);
				setMunicipalities(municipalities);
				setSponsors(sponsors);
			} catch (error) {
				const message: string = (error as Error).name;
				const description: string = (error as Error).message;
				notification.error({ message, description });
			}

			setLoading(false);
		}

		getResources();
	}, []);

	async function handleUpdate(values: any): Promise<void> {
		setLoading(true);
		const imageTool = imageToolRef?.current;

		if (imageTool === null) {
			notification.error({ message: txt('notification.image missing') });
			setLoading(false);
			return;
		}

		const canvas = imageTool.getImage();
		const base64: string = canvas.toDataURL('image/jpeg');

		if (base64 !== initialImageSource) {
			values.image = base64;
		}

		try {
			await GroupService.update(group.id, { ...group, ...values });
			setGroup({ ...group, ...values });
			notification.success({ message: txt('notification.update successful') });
			history.push('/groups');
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	async function handleDeleteGroup(): Promise<void> {
		try {
			await GroupService.remove(id);
			notification.success({ message: txt('notification.deletion successful') });
			history.goBack();
			setShowDeleteModal(false);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}

	return (
		<StyledForm
			ref={formRef}
			initialValues={group}
			onFinish={handleUpdate}
			labelCol={{ span: 4 }}
			isMobileDevice={isMobileDevice}
		>
			<div className="form-content-container">
				<div className="title-container">
					<Form.Item name="name" label={txt('global.name')}>
						<Input required />
					</Form.Item>

					<Form.Item name="description" label={txt('global.description')}>
						<Input.TextArea required />
					</Form.Item>
				</div>

				<div className="details-container">
					<Form.Item label={txt('global.municipality')} name="municipality_id">
						<Select
							showSearch
							filterOption={false}
							onSearch={(target: string) => handleSearch(target, setMunicipalities)}
							dropdownMatchSelectWidth={false}
							onBlur={() => setMunicipalities(municipalities)}
						>
							{municipalitiesFiltered.map((municipality: Record<string, any>) => (
								<Select.Option key={municipality.id} value={municipality.id}>
									{municipality.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label="Type" name="type">
						<Select dropdownMatchSelectWidth={false}>
							{types.map((type: Record<string, any>) => (
								<Select.Option key={type.id} value={type.id}>
									{type.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item name="password" label={txt('global.password')}>
						<Input.Password
							type="password"
							iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
						/>
					</Form.Item>

					<Form.Item label={txt('group.sponsor')} name="sponsor_id">
						<Select
							showSearch
							filterOption={false}
							dropdownMatchSelectWidth={false}
							onSearch={(target: string) => handleSearch(target, setSponsors)}
							onBlur={() => setSponsors(sponsors)}
						>
							{sponsorsFiltered.map((sponsor: Record<string, any>) => (
								<Select.Option key={sponsor.id} value={sponsor.id}>
									{sponsor.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('group.data responsible')} name="responsible">
						<Select
							allowClear
							showSearch
							filterOption={false}
							dropdownMatchSelectWidth={false}
							onSearch={(target: string) => handleSearch(target, setSponsors)}
							onBlur={() => setSponsors(sponsors)}
						>
							{sponsorsFiltered.map((sponsor: Record<string, any>) => (
								<Select.Option key={sponsor.id} value={sponsor.id}>
									{sponsor.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label="Status" name="status_id">
						<Select dropdownMatchSelectWidth={false}>
							{statuses.map((status: Record<string, any>) => (
								<Select.Option key={status.id} value={status.id}>
									{status.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</div>

				<div className="image-container">
					<Form.Item label={txt('global.image')}>
						<ImageTool
							refProxy={imageToolRef}
							imageSource={image}
							setImageSource={setImage}
							initialImageSource={initialImageSource}
							setInitialImageSource={setInitialImageSource}
						/>
					</Form.Item>
				</div>
			</div>

			<Form.Item>
				<div className="submit-container">
					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="primary"
						htmlType="submit"
						loading={loading}
					>
						{txt('global.update')} <CheckOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="ghost"
						danger
						onClick={() => setShowDeleteModal(true)}
						className="delete-button"
					>
						{txt('global.delete')} <DeleteFilled />
					</Button>
				</div>
			</Form.Item>

			<DeleteModal useVisible={[showDeleteModal, setShowDeleteModal]} onConfirm={handleDeleteGroup} />
		</StyledForm>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	display: flex;
	flex-direction: column;
	gap: 2em;

	.form-content-container {
		display: grid;
		gap: 1em;

		${({ isMobileDevice }) => {
			if (isMobileDevice) return;
			return 'grid-template-columns: repeat(auto-fill, minmax(350px, 1fr))';
		}}

		textarea {
			box-sizing: border-box;
			height: 100%;
			min-height: 250px;
			width: 100%;
			resize: none;
		}
	}

	.submit-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		.delete-button {
			margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		}
	}
`;
